import type { ReactNode } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { href } from '../app/router';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { configureAmplify, loadAuthConfig } from './config';
import { getAccessToken, saveReturnPath, takeReturnPath } from './session';

export type AuthBootstrapResult =
  | { status: 'disabled' }
  | { status: 'ready' }
  | { status: 'redirecting' }
  | { status: 'signed-out' }
  | { status: 'config-error'; missingKeys: string[] }
  | { status: 'callback-error'; message: string };

// code交換が終わらないまま画面が固まるのを避けるための上限。
export const OAUTH_TIMEOUT_MS = 15_000;

const CALLBACK_ERROR_MESSAGE = '認証に失敗しました。もう一度ログインしてください。';
const CALLBACK_TIMEOUT_MESSAGE =
  'ログイン処理がタイムアウトしました。もう一度ログインしてください。';

type OAuthOutcome = { ok: true } | { ok: false; message: string };

/**
 * 認証の初期化。Appをmountする前に完了させる。
 *
 * /auth/callback?code=... のままAppをmountすると、routerはこのURLを知らないので
 * notFoundを描き、各ページはtokenの無いまま実APIを叩き始める。Reactのeffectで
 * 交換するとStrictModeの二重実行で使い捨てのcodeを2回使うことにもなる。
 * そのため交換はReactの外（main.tsxから1回だけ呼ぶこの関数）で完結させ、
 * この関数が返った後に初めて描画する。
 */
export async function runAuthBootstrap(
  env: ImportMetaEnv = import.meta.env,
): Promise<AuthBootstrapResult> {
  const loaded = loadAuthConfig(env);
  if (!loaded.ok) {
    return { status: 'config-error', missingKeys: loaded.error.missingKeys };
  }
  const config = loaded.config;
  if (config.mode === 'disabled') {
    return { status: 'disabled' };
  }

  const url = new URL(window.location.href);
  const callbackPath = new URL(config.redirectSignIn).pathname;
  const signedOutPath = new URL(config.redirectSignOut).pathname;
  const isCallback =
    url.pathname === callbackPath &&
    (url.searchParams.has('code') || url.searchParams.has('error'));

  if (isCallback && url.searchParams.has('error')) {
    // Cognitoから返ったエラーの内容はそのまま画面へ出さない
    return { status: 'callback-error', message: CALLBACK_ERROR_MESSAGE };
  }

  // Hubの購読はconfigureAmplifyより先に行う。Amplifyはconfigureの直後に
  // callbackの検出を始めるため、後から購読するとイベントを取り逃がしうる。
  const outcome = isCallback ? waitForOAuthOutcome() : null;
  configureAmplify(config);

  if (outcome) {
    const result = await outcome;
    if (!result.ok) {
      return { status: 'callback-error', message: result.message };
    }
    // codeを含むcallback URLを履歴に残さず、ログイン前の画面へ戻す
    window.history.replaceState(null, '', takeReturnPath());
    return { status: 'ready' };
  }

  const token = await getAccessToken();
  if (token) return { status: 'ready' };

  // ログアウト直後の着地点。ここで自動再ログインすると、ログアウトしたのに
  // 即ログイン画面へ戻って完了が見えない。
  if (url.pathname === signedOutPath) return { status: 'signed-out' };

  saveReturnPath(url.pathname + url.search);
  await signInWithRedirect();
  // signInWithRedirectは遷移を「開始」した時点でresolveする。ここでreadyを返すと
  // 遷移が終わるまでの間、未認証のままAppが描画されて見えてしまう。
  return { status: 'redirecting' };
}

function waitForOAuthOutcome(): Promise<OAuthOutcome> {
  return new Promise((resolve) => {
    // 購読解除関数は変数へ直接束縛しない。Hubがイベントを同期的に配信すると
    // Hub.listenが返る前にコールバックが走り、constの一時的デッドゾーンに
    // 触れてReferenceErrorになる。
    let unsubscribe: (() => void) | undefined;
    let settled = false;

    const settle = (outcome: OAuthOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve(outcome);
    };

    const timer = setTimeout(
      () => settle({ ok: false, message: CALLBACK_TIMEOUT_MESSAGE }),
      OAUTH_TIMEOUT_MS,
    );

    unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        settle({ ok: true });
      } else if (payload.event === 'signInWithRedirect_failure') {
        settle({ ok: false, message: CALLBACK_ERROR_MESSAGE });
      }
    });
    // 同期配信だった場合はこの時点でsettled=true。unsubscribeを取り逃がさない。
    if (settled) unsubscribe();
  });
}

/** 本体を出せないときの画面。notFoundと同じくWorkspaceShellの枠にEmptyStateを置く。 */
function AuthScreen(props: { title: string; description: string; action?: ReactNode }) {
  return (
    <WorkspaceShell brandHref={href({ name: 'workspaces' })}>
      <EmptyState {...props} />
    </WorkspaceShell>
  );
}

function LoginButton({ children }: { children: string }) {
  return (
    <Button variant="primary" onClick={() => void signInWithRedirect()}>
      {children}
    </Button>
  );
}

/**
 * runAuthBootstrapの結果を描画する。副作用を持たない純粋な分岐。
 * 初期化そのものをここで行わない理由はrunAuthBootstrapのコメントの通り。
 */
export function AuthBoundary({
  result,
  children,
}: {
  result: AuthBootstrapResult;
  children: ReactNode;
}) {
  switch (result.status) {
    case 'disabled':
    case 'ready':
      return children;
    case 'redirecting':
      // Managed Loginへの遷移が終わるまでの間にAppが見えるとフラッシュになる
      return null;
    case 'config-error':
      return (
        <AuthScreen
          title="認証の設定が不足しています"
          // VITE_* はビルド時に埋め込まれるので、直すには再ビルドが要る
          description={`ビルド時の環境変数を確認してください: ${result.missingKeys.join(', ')}`}
        />
      );
    case 'callback-error':
      return (
        <AuthScreen
          title="ログインできませんでした"
          description={result.message}
          action={<LoginButton>ログインし直す</LoginButton>}
        />
      );
    case 'signed-out':
      return (
        <AuthScreen
          title="ログアウトしました"
          description="続けて利用する場合は再度ログインしてください。"
          action={<LoginButton>ログインする</LoginButton>}
        />
      );
  }
}
